import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { colors as dsColors, mediaQuery } from '@modernhealth/vital-foundation';
import { useMediaQuery } from '@react-hook/media-query';
import clsx from 'clsx';
import { Icon } from '../../Icon/Icon';
import { SkeletonPlaceholder } from '../../Skeleton/SkeletonPlaceholder';
import { DeprecatedReactRouterLink as Link } from '../../Link/DeprecatedReactRouterLink';
import { BodyTextSmall, BodyTextXSmall, HeadingText10, HeadingText30, } from '../../Typography/Typography';
import * as styles from './ContentCard.module.scss';
export var ContentCardTestId;
(function (ContentCardTestId) {
    ContentCardTestId["Completed"] = "ContentCardTestId.Completed";
    ContentCardTestId["Locked"] = "ContentCardTestId.Locked";
})(ContentCardTestId || (ContentCardTestId = {}));
// the colors are duplicated so we can assign them to numbers from 0 to 9
const possibleColors = [
    dsColors.yellow05,
    dsColors.orange05,
    dsColors.blue20,
    dsColors.cyan05,
    dsColors.orange20,
    dsColors.yellow05,
    dsColors.orange05,
    dsColors.blue20,
    dsColors.cyan05,
    dsColors.orange20,
];
const symbolsToRemove = /\D[^.-]/g;
const reduceNumber = (bigNumber) => bigNumber
    .toString()
    .split('')
    .map(Number)
    .reduce((a, b) => a + b);
const hashUuid = (id) => {
    // substring is used here to get more variety of numbers,
    // without it the numbers were too repetitive resulting in the same bg colors being used next to each other
    let hashedNumber = parseInt(id.substring(2, id.length - 3).replace(symbolsToRemove, ''));
    do {
        hashedNumber = reduceNumber(hashedNumber);
    } while (hashedNumber > 9);
    return hashedNumber;
};
const ContentCardContent = ({ id, image, title, description, imageInCircle = false, eyebrowText, descriptionStartIconUrl, isCompleted, backgroundColor, progressBarValue, showNewContentIndicator = false, }) => {
    const smallScreenDetected = useMediaQuery(mediaQuery.maxWidth('sm', true));
    const TitleComponent = smallScreenDetected ? HeadingText10 : HeadingText30;
    const DescriptionComponent = smallScreenDetected
        ? BodyTextXSmall
        : BodyTextSmall;
    const [bgColorToUse, setBgColorToUse] = useState(backgroundColor);
    useEffect(() => {
        if (!backgroundColor) {
            setBgColorToUse(possibleColors[hashUuid(id)]);
        }
    }, [backgroundColor, id]);
    const progressValue = typeof progressBarValue === 'number' &&
        Math.round(Math.max(0, Math.min(progressBarValue, 1)) * 100);
    const imageStyles = imageInCircle ? styles.circleImage : styles.image;
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.imageContainer, style: {
                    backgroundColor: bgColorToUse,
                }, children: [isCompleted ? (_jsx(Icon, { className: styles.icon, "data-testid": ContentCardTestId.Completed, name: "checkmark-enclosed-filled", color: "cyan40" })) : null, image ? _jsx("img", { className: imageStyles, src: image, alt: "" }) : null, typeof progressValue === 'number' ? (_jsx("div", { className: styles.progressBarOuter, role: "progressbar", "aria-label": "Progress", "aria-valuenow": progressValue, "aria-valuemin": 0, "aria-valuemax": 100, children: _jsx("div", { className: styles.progressBarInner, style: { width: progressValue || undefined } }) })) : null] }), _jsxs("div", { className: styles.contentWrapper, children: [eyebrowText ? (_jsx(BodyTextXSmall, { fontColor: "gray65", marginBottom: 4, children: eyebrowText })) : null, _jsxs("div", { className: styles.titleWrapper, children: [showNewContentIndicator ? (_jsx("div", { className: styles.newContentIndicator })) : null, _jsx(TitleComponent, { numberOfLines: 2, marginBottom: 8, textIndent: showNewContentIndicator ? 15 : 0, children: title })] }), _jsxs("div", { className: styles.descriptionWrapper, children: [descriptionStartIconUrl ? (_jsx("img", { alt: "", className: styles.descriptionStartIcon, src: descriptionStartIconUrl })) : null, _jsx(DescriptionComponent, { fontColor: "gray65", marginBottom: 0, children: description })] })] })] }));
};
const Name = 'ContentCard';
export const ContentCard = ({ linkTo, onClick, className, testID, trackClickCallback, ...props }) => {
    const linkContainerStyles = clsx([styles.container, className]);
    if (linkTo) {
        return (_jsx(Link, { onDragStart: evt => evt.preventDefault(), draggable: false, to: linkTo, className: linkContainerStyles, "data-testid": testID, "data-component": Name, onClick: trackClickCallback, children: _jsx(ContentCardContent, { ...props }) }));
    }
    else if (onClick) {
        const clickableContainerStyles = clsx([
            styles.container,
            styles.clickable,
            className,
        ]);
        return (_jsx("div", { onDragStart: evt => evt.preventDefault(), draggable: false, onClick: trackClickCallback
                ? () => {
                    onClick();
                    trackClickCallback();
                }
                : onClick, className: clickableContainerStyles, "data-testid": testID, "data-component": Name, children: _jsx(ContentCardContent, { ...props }) }));
    }
    else {
        const containerStyles = clsx([styles.container, className]);
        return (_jsx("div", { onDragStart: evt => evt.preventDefault(), draggable: false, className: containerStyles, "data-testid": testID, "data-component": Name, children: _jsx(ContentCardContent, { ...props }) }));
    }
};
export const ContentCardSkeleton = ({ className, }) => {
    const smallScreenDetected = useMediaQuery(mediaQuery.maxWidth('md', true));
    const containerStyles = clsx([styles.container, className]);
    return (_jsxs("div", { className: containerStyles, children: [_jsx(SkeletonPlaceholder, { width: "100%", height: 80, marginBottom: smallScreenDetected ? 8 : 12 }), _jsx(SkeletonPlaceholder, { width: "60%", height: 20, marginBottom: smallScreenDetected ? 4 : 8 }), _jsx(SkeletonPlaceholder, { width: "80%", height: 20 })] }));
};
